/**
 * This component contains the Filter Controls pill carousel
 *
 * @module views/components/FiltersControl
 * @memberof -Common
 */

import React, { useCallback } from 'react';

import PropTypes from 'prop-types';

import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import { FILTER_GROUPS } from '@ulta/utils/constants/filters';

import { getStoreFromFilters } from '../FilterGroup/FilterGroup';
import PillCarousel, { PILL_TYPES } from '../PillCarousel/PillCarousel';
import * as utils from './FiltersControl';

/**
 * Represents a FiltersControl component
 * @method
 * @param {FiltersControlProps} props - React properties passed from composition
 * @returns FiltersControl
 */

export const FiltersControl = function( props ){
  const {
    filterByStorePill,
    isFilterShown,
    setIsFilterShown,
    showFilterLabel,
    displayArrows,
    invokeAction,
    isMobileDevice,
    filterbuttonArialabel,
    showFiltersButtonRef,
    appliedFilters
  } = props;

  const { store } = useUserContext();

  const getStoreAction = useCallback(
    utils.composeGetStoreAction(
      { filterByStorePill, store }
    ),
    [filterByStorePill, store]
  );

  const handleStoreLabel = useCallback(
    utils.transformStoreSelected(
      { filterByStorePill, store }
    ),
    [filterByStorePill, store]
  );

  const handleOnClick = useCallback(
    utils.composeOnClick(
      { store },
      { invokeAction }
    ), [store] );

  const appliedFilterPills = useCallback(
    utils.composeFilterPills(
      { filterByStorePill, showFilterLabel, isFilterShown, store, isMobileDevice, showFiltersButtonRef, filterbuttonArialabel, appliedFilters },
      { setIsFilterShown, getStoreAction, handleStoreLabel, handleOnClick, getStoreFromFilters }
    ),
    [isFilterShown, filterByStorePill, store, showFilterLabel, getStoreAction, handleStoreLabel, handleOnClick, getStoreFromFilters]
  );

  return (
    <PillCarousel
      items={ appliedFilterPills() }
      pillType={ PILL_TYPES.FILTER }
      displayArrows={ displayArrows }
    />
  );
};

/**
 * Composes an array of filter pill items to be used in a UI component,
 * combining filter and store-related logic for display and interaction.
 *
 * @function composeFilterPills
 * @param {Object} data - An object containing filter-related data.
 * @param {Object} methods - An object containing handler functions for managing filter behavior.
 * @returns {Function} A function that returns an array of filter pill items when executed.
 * @param {Object} data.store - Store stored in local storage from UserContext.
 * @param {array} data.appliedFilters - array containing applied filters.
 * @property {string} type - The type of pill (e.g., 'FilterHeader', 'StoreFilter', 'SelectStore').
 * @property {boolean} [ariaExpanded] - Indicates whether the filter section is expanded (for 'FilterHeader' type).
 * @property {string} [filterbuttonArialabel] - The aria-label for the filter button.
 * @property {string} [iconImage] - The icon name or image associated with the pill.
 * @property {boolean} [isMobileDevice] - Indicates if the current device is mobile.
 * @property {string} name - The name or label of the pill.
 * @property {Function} [onPillClick] - A callback function for pill click events.
 * @property {boolean} [isStoreChecked] - Indicates if a store is currently selected.
 * @property {Object} [isSelectedStore] - The selected store details, if any.
 * @property {string} [closeIcon] - The icon to display for closing the pill.
 * @property {Function} [onClick] - A callback function for handling click events (e.g., selecting a store).
 * @property {Function} [selectAction] - The action to execute for the pill's functionality.
 */
export const composeFilterPills = ( data, methods ) => () => {
  const {
    filterByStorePill,
    showFilterLabel,
    isFilterShown,
    store,
    appliedFilters,
    isMobileDevice,
    showFiltersButtonRef,
    filterbuttonArialabel
  } = handleEmptyObjects( data );
  const {
    setIsFilterShown,
    getStoreAction,
    handleStoreLabel,
    handleOnClick,
    getStoreFromFilters
  } = handleEmptyObjects( methods );

  if( !showFilterLabel || !setIsFilterShown || !getStoreAction ||
    !handleStoreLabel || !handleOnClick ){
    return {};
  }

  const storeFilter = appliedFilters?.find(
    ( filter ) => filter?.groupType === FILTER_GROUPS.AVAILABLE_STORE_IDS
  );

  const { bopisStoreId: storeId, bopisStoreName: storeName } = store || {};

  const storeFromFilters = getStoreFromFilters( {
    storeFilter,
    storeId,
    storeName
  } );

  const selectedStore = storeFromFilters || store;

  const filterItems = [
    {
      type: 'FilterHeader',
      ariaExpanded: isFilterShown,
      filterbuttonArialabel: filterbuttonArialabel,
      iconImage: 'Filters',
      isMobileDevice: isMobileDevice,
      name: showFilterLabel,
      onPillClick: () => setIsFilterShown( !isFilterShown ),
      isStoreChecked: filterByStorePill?.storeSelected,
      isSelectedStore: selectedStore,
      showFilterLabel: showFilterLabel,
      showFiltersButtonRef: showFiltersButtonRef,
      filterByStorePill: filterByStorePill
    },
    ...( filterByStorePill ? [{
      type: 'StoreFilter',
      closeIcon: filterByStorePill?.closeIcon,
      iconImage: filterByStorePill?.selectStoreIcon,
      name: filterByStorePill?.selectStoreLabel,
      onClick: handleOnClick,
      selectAction: getStoreAction(),
      isSelectedStore: selectedStore,
      isStoreChecked: filterByStorePill?.storeSelected
    }] : [] ),
    ...( selectedStore && filterByStorePill?.storeSelected ? [{
      type: 'SelectStore',
      iconImage: filterByStorePill?.selectedStoreIcon,
      name: handleStoreLabel(),
      selectAction: filterByStorePill?.openStoreOverlayAction,
      isSelectedStore: selectedStore
    }] : [] )
  ];

  return filterItems;
};

/**
   * Method to handle invoking action from DXL for PillCarousel pills
   * @param {Object} data.store - Store stored in local storage from UserContext.
   * @param {object} methods methods
   * @param {action} methods.invokeAction handler from layer host to invoke DXL action
   * @param {string} action DXL action
   */
export const composeOnClick = ( data, methods ) => ( selectAction ) => {
  const { store } = data || {};
  const { invokeAction } = methods || {};

  if( !invokeAction ){
    return;
  }

  const hasStoreId = selectAction.params.includes( 'storeId' );
  const variables = {};

  if( hasStoreId ){
    variables.moduleParams = { storeId: store?.bopisStoreId };
  }

  invokeAction( {
    ...selectAction,
    variables
  } );
};

/**
 * Transforms the selected store label by splitting the label and extracting the first part.
 *
 * @param {Object} data - The data containing the store selection details.
 * @param {Object} data.filterByStorePill - The object representing the store filter pill, including label details.
 * @param {string} data.filterByStorePill.selectedStoreLabel - The label for the selected store, which contains '%1' as a placeholder.
 * @param {Object} data.store - Store stored in local storage from UserContext.
 * @returns {string|undefined} The first part of the transformed selected store label or undefined if input data is incomplete.
 */
export const transformStoreSelected = ( data ) => () => {
  const { filterByStorePill, store } = handleEmptyObjects( data );

  if( !filterByStorePill || !store ){
    return;
  }

  const transformedStoreSelectedLabel = filterByStorePill?.selectedStoreLabel.split( '%1' );
  const transformedStoreSelected = transformedStoreSelectedLabel[0];

  return transformedStoreSelected;
};

/**
 * @function composeGetStoreAction
 * @description Composes a function to determine and return the appropriate store action based on selection state.
 * @param {Object} data - An object containing the parameters required for the function.
 * @param {Object} data.store - Store stored in local storage from UserContext.
 * @param {Function} data.filterByStorePill.removeStoreAction - Action function to remove the selected store.
 * @param {Function} data.filterByStorePill.openStoreOverlayAction - Action function to open the store overlay.
 * @param {Function} data.filterByStorePill.applyStoreAction - Action function to apply the store selection.
 * @returns {Function} - A function that, when invoked, returns the appropriate action based on the selection state.
 */
export const composeGetStoreAction = ( data ) => () => {
  const { store, filterByStorePill } = handleEmptyObjects( data );

  if( !filterByStorePill ){
    return {};
  }

  if( filterByStorePill?.storeSelected && store ){
    return filterByStorePill?.removeStoreAction;
  }

  if( store ){
    return filterByStorePill?.applyStoreAction;
  }

  return filterByStorePill?.openStoreOverlayAction;
};

/**
  * Property type definitions
  * @typedef FiltersControlProps
  * @type {object}
  * @property {array} items - This is array of items
  * @property {boolean} displayArrows - flag to display swiper Pagination
  * @property {function} onPillClick - function to handle on pill click
  * @property {array} appliedFilters - This is an array of applied filters
  */

export const propTypes =  {
  items: PropTypes.arrayOf( PropTypes.shape(
    {
      name: PropTypes.string,
      selected: PropTypes.bool,
      selectAction: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string,
        graphql: PropTypes.string
      } )
    }
  ) ),
  displayArrows: PropTypes.bool,
  onPillClick: PropTypes.func,
  appliedFilters: PropTypes.array
};

FiltersControl.propTypes = propTypes;

export default FiltersControl;